/**
 * Squad Operations Dashboard
 *
 * Builder-facing page for managing operational agent activity across all 5 squads.
 * Shows proposals needing approval, cross-squad messages, autonomy configs, and agent rules.
 *
 * Consumes: /api/squad-ops/* endpoints (backend-merged/routes/squad-ops.js)
 * Date: 2026-03-30
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  ArrowUpCircle,
  MessageSquare,
  Brain,
  Activity,
  Shield,
  ChevronRight,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { format, formatDistanceToNow } from 'date-fns';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Proposal {
  id: string;
  squad: string;
  action_type: string;
  autonomy_level: string;
  site_id: string;
  summary: string;
  evidence: Record<string, unknown>;
  risk_assessment: Record<string, unknown>;
  estimated_cost: Record<string, unknown>;
  status: string;
  created_at: string;
  expires_at: string;
  reviewed_by: string | null;
  review_notes: string | null;
}

interface SquadMessage {
  id: string;
  source_squad: string;
  target_squad: string;
  message_type: string;
  priority: string;
  site_id: string;
  message: string;
  suggested_action: string;
  status: string;
  created_at: string;
}

interface AutonomyConfig {
  squad: string;
  action_type: string;
  autonomy_level: string;
  consecutive_approvals: number;
  total_executions: number;
  successful_executions: number;
  success_rate: number | null;
  notes: string | null;
}

interface AgentRule {
  id: string;
  squad: string;
  rule_name: string;
  rule_type: string;
  condition: Record<string, unknown>;
  action: string;
  confidence: number;
  times_triggered: number;
  times_successful: number;
  success_rate: number | null;
  is_active: boolean;
}

interface DashboardSummary {
  squad: string;
  proposals: Record<string, number>;
  messages: Record<string, number>;
  rules: { total: number; active: number; avg_success: number | null };
  observations: { total: number; latest: string | null };
}

// ─── Constants ──────────────────────────────────────────────────────────────

const SQUADS = [
  { id: 'demand-studio', label: 'Demand', color: 'bg-blue-500' },
  { id: 'offering-studio', label: 'Offering', color: 'bg-green-500' },
  { id: 'answer-studio', label: 'Answer', color: 'bg-purple-500' },
  { id: 'conversation-studio', label: 'Conversation', color: 'bg-orange-500' },
  { id: 'impact-studio', label: 'Impact', color: 'bg-red-500' },
];

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'destructive',
  high: 'default',
  medium: 'secondary',
  low: 'outline',
};

const LEVEL_COLORS: Record<string, string> = {
  automatic: 'bg-green-100 text-green-800',
  notify: 'bg-yellow-100 text-yellow-800',
  propose: 'bg-blue-100 text-blue-800',
};

// ─── Component ──────────────────────────────────────────────────────────────

export default function SquadOps() {
  const [activeSquad, setActiveSquad] = useState('demand-studio');
  const [activeTab, setActiveTab] = useState('proposals');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [overview, setOverview] = useState<DashboardSummary[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [messages, setMessages] = useState<SquadMessage[]>([]);
  const [autonomy, setAutonomy] = useState<AutonomyConfig[]>([]);
  const [rules, setRules] = useState<AgentRule[]>([]);

  // Dialog state
  const [reviewDialog, setReviewDialog] = useState<{ proposal: Proposal; action: 'approve' | 'reject' } | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [resolveDialog, setResolveDialog] = useState<SquadMessage | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');

  // ─── Fetchers ───────────────────────────────────────────────────────────

  const fetchOverview = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/squad-ops/overview`);
      if (res.ok) {
        const data = await res.json();
        setOverview(data.squads || []);
      }
    } catch (e) {
      console.error('Failed to fetch overview', e);
    }
  }, []);

  const fetchSquadData = useCallback(async (squad: string) => {
    setLoading(true);
    setError(null);
    try {
      const [propRes, msgRes, autoRes, ruleRes] = await Promise.all([
        authFetch(`${API_URL}/squad-ops/${squad}/proposals?status=pending`),
        authFetch(`${API_URL}/squad-ops/${squad}/messages`),
        authFetch(`${API_URL}/squad-ops/${squad}/autonomy`),
        authFetch(`${API_URL}/squad-ops/${squad}/rules`),
      ]);

      if (propRes.ok) setProposals((await propRes.json()).proposals || []);
      if (msgRes.ok) setMessages((await msgRes.json()).messages || []);
      if (autoRes.ok) setAutonomy((await autoRes.json()).configs || []);
      if (ruleRes.ok) setRules((await ruleRes.json()).rules || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    fetchSquadData(activeSquad);
  }, [activeSquad, fetchSquadData]);

  // ─── Actions ────────────────────────────────────────────────────────────

  const handleReview = async () => {
    if (!reviewDialog) return;
    const { proposal, action } = reviewDialog;
    try {
      const res = await authFetch(`${API_URL}/squad-ops/${proposal.squad}/proposals/${proposal.id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: reviewNotes }),
      });
      if (res.ok) {
        setReviewDialog(null);
        setReviewNotes('');
        fetchSquadData(activeSquad);
        fetchOverview();
      }
    } catch (e) {
      console.error('Review failed', e);
    }
  };

  const handleAcknowledge = async (msg: SquadMessage) => {
    try {
      await authFetch(`${API_URL}/squad-ops/${msg.target_squad}/messages/${msg.id}/acknowledge`, { method: 'POST' });
      fetchSquadData(activeSquad);
    } catch (e) {
      console.error('Acknowledge failed', e);
    }
  };

  const handleResolve = async () => {
    if (!resolveDialog) return;
    try {
      await authFetch(`${API_URL}/squad-ops/${resolveDialog.target_squad}/messages/${resolveDialog.id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: resolveNotes }),
      });
      setResolveDialog(null);
      setResolveNotes('');
      fetchSquadData(activeSquad);
    } catch (e) {
      console.error('Resolve failed', e);
    }
  };

  const handlePromote = async (config: AutonomyConfig) => {
    try {
      await authFetch(`${API_URL}/squad-ops/${config.squad}/autonomy/${config.action_type}/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: 'Promoted via admin dashboard' }),
      });
      fetchSquadData(activeSquad);
    } catch (e) {
      console.error('Promote failed', e);
    }
  };

  // ─── Overview Cards ─────────────────────────────────────────────────────

  const activeSquadInfo = SQUADS.find((s) => s.id === activeSquad);
  const totalPending = overview.reduce((sum, s) => sum + (s.proposals?.pending || 0), 0);
  const totalMessages = overview.reduce((sum, s) => sum + (s.messages?.open || 0) + (s.messages?.acknowledged || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Squad Operations</h1>
          <p className="text-muted-foreground">Manage operational agent proposals, messages, and autonomy levels</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchOverview(); fetchSquadData(activeSquad); }}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Overview strip */}
      <div className="grid grid-cols-5 gap-3">
        {SQUADS.map((sq) => {
          const data = overview.find((o) => o.squad === sq.id);
          const pending = data?.proposals?.pending || 0;
          const openMsgs = (data?.messages?.open || 0) + (data?.messages?.acknowledged || 0);
          return (
            <Card
              key={sq.id}
              className={`cursor-pointer transition-all ${activeSquad === sq.id ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
              onClick={() => setActiveSquad(sq.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${sq.color}`} />
                  <span className="font-medium text-sm">{sq.label}</span>
                </div>
                <div className="flex gap-3 text-xs text-muted-foreground">
                  {pending > 0 && <span className="text-amber-600 font-medium">{pending} pending</span>}
                  {openMsgs > 0 && <span className="text-blue-600 font-medium">{openMsgs} msgs</span>}
                  {pending === 0 && openMsgs === 0 && <span className="text-green-600">All clear</span>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary bar */}
      {(totalPending > 0 || totalMessages > 0) && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {totalPending > 0 && <span className="font-medium">{totalPending} proposals</span>}
            {totalPending > 0 && totalMessages > 0 && ' and '}
            {totalMessages > 0 && <span className="font-medium">{totalMessages} messages</span>}
            {' '}need attention across all squads.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Squad detail tabs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${activeSquadInfo?.color}`} />
            <CardTitle>{activeSquadInfo?.label} Studio</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="proposals" className="gap-1">
                <Shield className="h-3 w-3" />
                Proposals {proposals.length > 0 && <Badge variant="secondary" className="ml-1 h-5 px-1.5">{proposals.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="messages" className="gap-1">
                <MessageSquare className="h-3 w-3" />
                Messages {messages.length > 0 && <Badge variant="secondary" className="ml-1 h-5 px-1.5">{messages.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="autonomy" className="gap-1">
                <ArrowUpCircle className="h-3 w-3" />
                Autonomy
              </TabsTrigger>
              <TabsTrigger value="rules" className="gap-1">
                <Brain className="h-3 w-3" />
                Rules {rules.length > 0 && <Badge variant="secondary" className="ml-1 h-5 px-1.5">{rules.length}</Badge>}
              </TabsTrigger>
            </TabsList>

            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* ─── Proposals Tab ──────────────────────────────────────── */}
            <TabsContent value="proposals">
              {!loading && proposals.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  No pending proposals
                </div>
              )}
              <div className="space-y-3">
                {proposals.map((p) => (
                  <Card key={p.id} className="border-l-4 border-l-amber-400">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{p.action_type.replace(/_/g, ' ')}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${LEVEL_COLORS[p.autonomy_level] || ''}`}>
                              {p.autonomy_level}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">{p.summary}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            <span><Clock className="inline h-3 w-3 mr-1" />{formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}</span>
                            {p.expires_at && <span>Expires {format(new Date(p.expires_at), 'MMM d')}</span>}
                          </div>
                          {p.evidence && Object.keys(p.evidence).length > 0 && (
                            <details className="mt-2">
                              <summary className="text-xs cursor-pointer text-muted-foreground">View evidence</summary>
                              <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-auto max-h-40">
                                {JSON.stringify(p.evidence, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button size="sm" variant="default" onClick={() => { setReviewDialog({ proposal: p, action: 'approve' }); setReviewNotes(''); }}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setReviewDialog({ proposal: p, action: 'reject' }); setReviewNotes(''); }}>
                            <XCircle className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* ─── Messages Tab ───────────────────────────────────────── */}
            <TabsContent value="messages">
              {!loading && messages.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  No open messages
                </div>
              )}
              <div className="space-y-3">
                {messages.map((m) => (
                  <Card key={m.id} className={`border-l-4 ${m.priority === 'critical' ? 'border-l-red-500' : m.priority === 'high' ? 'border-l-orange-400' : 'border-l-blue-300'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant={PRIORITY_COLORS[m.priority] as any || 'secondary'}>{m.priority}</Badge>
                            <span className="font-medium text-sm">{m.message_type.replace(/_/g, ' ')}</span>
                            <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">from {m.source_squad.replace('-studio', '')}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{m.message}</p>
                          {m.suggested_action && (
                            <p className="text-xs text-blue-600 mt-1">Suggested: {m.suggested_action}</p>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <div className="flex gap-2 ml-4">
                          {m.status === 'open' && (
                            <Button size="sm" variant="outline" onClick={() => handleAcknowledge(m)}>Acknowledge</Button>
                          )}
                          <Button size="sm" variant="default" onClick={() => { setResolveDialog(m); setResolveNotes(''); }}>Resolve</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* ─── Autonomy Tab ───────────────────────────────────────── */}
            <TabsContent value="autonomy">
              <div className="space-y-2">
                <div className="grid grid-cols-6 gap-2 text-xs font-medium text-muted-foreground px-2 pb-1 border-b">
                  <span className="col-span-2">Action Type</span>
                  <span>Level</span>
                  <span>Approvals</span>
                  <span>Success</span>
                  <span></span>
                </div>
                {autonomy.map((c) => {
                  const canPromote = c.autonomy_level !== 'automatic' && c.consecutive_approvals >= 10 &&
                    (c.success_rate === null || c.success_rate >= 90);
                  return (
                    <div key={c.action_type} className="grid grid-cols-6 gap-2 items-center px-2 py-2 text-sm hover:bg-muted/50 rounded">
                      <span className="col-span-2 font-mono text-xs">{c.action_type}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full w-fit ${LEVEL_COLORS[c.autonomy_level] || ''}`}>
                        {c.autonomy_level}
                      </span>
                      <span className="text-xs">{c.consecutive_approvals} consecutive</span>
                      <span className="text-xs">
                        {c.success_rate !== null ? `${c.success_rate}%` : '—'}
                        {c.total_executions > 0 && <span className="text-muted-foreground"> ({c.total_executions})</span>}
                      </span>
                      <span>
                        {canPromote && (
                          <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => handlePromote(c)}>
                            <ArrowUpCircle className="h-3 w-3 mr-1" /> Promote
                          </Button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            {/* ─── Rules Tab ──────────────────────────────────────────── */}
            <TabsContent value="rules">
              {!loading && rules.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Brain className="h-8 w-8 mx-auto mb-2" />
                  No agent rules learned yet
                </div>
              )}
              <div className="space-y-2">
                {rules.map((r) => (
                  <Card key={r.id}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{r.rule_name}</span>
                            <Badge variant="outline" className="text-xs">{r.rule_type}</Badge>
                            {!r.is_active && <Badge variant="destructive" className="text-xs">inactive</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground">{r.action}</p>
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          <div>Confidence: {(r.confidence * 100).toFixed(0)}%</div>
                          <div>Triggered: {r.times_triggered}x</div>
                          {r.success_rate !== null && <div>Success: {r.success_rate}%</div>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* ─── Review Dialog ────────────────────────────────────────────── */}
      <Dialog open={!!reviewDialog} onOpenChange={(open) => !open && setReviewDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{reviewDialog?.action === 'approve' ? 'Approve' : 'Reject'} Proposal</DialogTitle>
            <DialogDescription>
              {reviewDialog?.proposal.action_type.replace(/_/g, ' ')} — {reviewDialog?.proposal.summary}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={reviewDialog?.action === 'reject' ? 'Rejection reason (required)' : 'Notes (optional)'}
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReviewDialog(null)}>Cancel</Button>
            <Button
              variant={reviewDialog?.action === 'approve' ? 'default' : 'destructive'}
              onClick={handleReview}
              disabled={reviewDialog?.action === 'reject' && !reviewNotes.trim()}
            >
              {reviewDialog?.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Resolve Dialog ───────────────────────────────────────────── */}
      <Dialog open={!!resolveDialog} onOpenChange={(open) => !open && setResolveDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Message</DialogTitle>
            <DialogDescription>
              {resolveDialog?.message_type.replace(/_/g, ' ')} from {resolveDialog?.source_squad.replace('-studio', '')}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Resolution notes"
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setResolveDialog(null)}>Cancel</Button>
            <Button onClick={handleResolve}>Resolve</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
